import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, defer, of } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';

import { AuthStatus } from '../entity/auth-status';
import { RoleLabel, User } from '../entity/user';
import { UserProperties, UserPropertiesPatch } from '../entity/user-properties';
import { AuthActions } from '../state/auth/auth.actions';
import { toUserTypeTransformer } from '../state/transformers/transformers';
import {
  AuthFirmType,
  LoginProviderType,
  RoleLabelType,
  SsoLoginResponse,
  UserType,
  UserTypeWithFirm,
} from '../state/types';
import { UserSettingsStorage } from '../storage/user.settings.storage';
import { BLService } from './bl.service';

@Injectable({
  providedIn: 'root',
})
export class UserService extends BLService {
  private RESOURCE_ENDPOINT = 'user';
  private currentUserSubject = new BehaviorSubject<User>(null);
  private uss = new UserSettingsStorage();

  constructor() {
    super();
    this.useCache();
  }

  loadCurrentUserType(): Observable<UserTypeWithFirm | AuthStatus> {
    let path = 'login/curity';

    if (this.uss.loadSetting(UserSettingsStorage.LOGIN_PROVIDER) === LoginProviderType.LEGACY) {
      path = `${this.RESOURCE_ENDPOINT}/me`;
    }

    const successPath$ = (response: SsoLoginResponse) =>
      of(response).pipe(
        tap((response: SsoLoginResponse) => this.setFirms(response.firms)),
        tap((response) => this.persistAssetAndFirmToStorage(response)),
        map((response) => this.ssoResponseToUserType(response)),
        tap((user) => this.currentUserSubject.next(user as unknown as User)),
      );

    return this.httpGet<SsoLoginResponse>(path, this.httpAuthHeader.getHeader()).pipe(
      switchMap((response) => {
        if (response.status === AuthStatus.OK) {
          return successPath$(response);
        }
        return of(response.status);
      }),
    );
  }

  reloadWithSelectedFirm(firmId: number): Observable<UserTypeWithFirm> {
    const path = 'login/curity';
    return this.httpGet<SsoLoginResponse>(path, this.httpAuthHeader.getHeader({ 'X-Firm-Id': firmId.toString() })).pipe(
      tap((response) => this.setFirms(response.firms)),
      tap((response) => this.persistAssetAndFirmToStorage(response)),
      map((response) => this.ssoResponseToUserType(response)),
      tap((user) => this.currentUserSubject.next(user as unknown as User)),
    );
  }

  getCurrentUser(): Observable<User> {
    return this.currentUserSubject.asObservable().pipe(
      filter((user: User) => !!user),
      take(1),
    );
  }

  patchCurrentUserProperties(changes: UserPropertiesPatch): Observable<UserProperties> {
    return defer(() => {
      const user = this.currentUserSubject.getValue();
      return this.httpPatch<UserProperties>(`${this.RESOURCE_ENDPOINT}/properties`, changes).pipe(
        tap((properties: UserProperties) => {
          this.clearCache();
          const currentUser = this.currentUserSubject.getValue();
          if (
            user &&
            currentUser?.id === user.id &&
            currentUser.firm.corporateIdentity === user.firm.corporateIdentity
          ) {
            this.currentUserSubject.next(User.from({ ...currentUser, properties: { ...properties } }));
          }
        }),
      );
    });
  }

  getAllUsers(): Observable<User[]> {
    return this.httpGet<User[]>(this.RESOURCE_ENDPOINT).pipe(
      map(
        (result) =>
          result.map((user) => ({
            ...user,
            roleLabel: (RoleLabel as Record<string, RoleLabel>)[user.role],
          })) as User[],
      ),
      tap((result) => this.updateCache(result)),
    );
  }

  getAllUserTypes(): Observable<UserType[]> {
    // very important to transform to usertype objects to separate objects in state (readonly) from objects in dialogs (mutable)
    // when all dialogs and views use state then we don't need transformers any more
    return this.getAllUsers().pipe(map((users) => users.map(toUserTypeTransformer.transform)));
  }

  getUsers(): Observable<User[]> {
    return this.getAllUsers().pipe(map((users) => users.filter((user) => user.active)));
  }

  getUser(id: number): Observable<User> {
    if (!this.hasCacheExpired() && this.cacheContainsUser(id)) {
      return this.getCacheObservable<User[]>().pipe(
        map((users: User[]) => {
          return users.find((user) => user.id === id);
        }),
      );
    }

    return this.httpGet<User>(`${this.RESOURCE_ENDPOINT}/${id}`);
  }

  saveUserType(user: UserType, errorHandler: any = tap(() => {})): Observable<UserType> {
    this.clearCache();
    const body = this.serializeUserProfile(user);
    return this.httpPut<UserType>(this.RESOURCE_ENDPOINT, body, undefined, (source) => source).pipe(
      map((storedUser) => ({
        ...storedUser,
        roleLabel: (RoleLabelType as Record<string, RoleLabelType>)[storedUser.role],
      })),
      errorHandler,
      this.catchErrorAndShowMessage(),
    );
  }

  saveUser(user: User, errorHandler: any = this.catchErrorAndShowMessage()): Observable<User> {
    this.clearCache();
    const body = this.serializeUserProfile(user);
    return this.httpPut<User>(this.RESOURCE_ENDPOINT, body, undefined, (source) => source).pipe(
      map((storedUser) => ({ ...storedUser, roleLabel: (RoleLabel as Record<string, RoleLabel>)[storedUser.role] })),
      errorHandler,
    );
  }

  logout(): void {
    this.clearCache();
    this.currentUserSubject.next(null);
  }

  private cacheContainsUser(id: number) {
    return this.getCacheValue<User[]>().some((user) => user.id === id);
  }

  private persistAssetAndFirmToStorage(value: SsoLoginResponse): void {
    const selectedFirm = value.firms.find((firm) => firm.selected);
    const selectedFirmUser = selectedFirm?.user;
    this.uss.saveSetting(UserSettingsStorage.LOGGED_IN_SERVICE, selectedFirmUser?.service);
    this.uss.saveSetting(UserSettingsStorage.LOGGED_IN_FIRM_NAME, selectedFirm?.name);
  }

  private ssoResponseToUserType(response: SsoLoginResponse): UserTypeWithFirm {
    const { firms, ...userProps } = response;
    const selectedFirm = firms.find((firm) => firm.selected);
    const selectedFirmUser = selectedFirm?.user;

    return {
      ...userProps,
      ...selectedFirmUser,
      roleLabel: (RoleLabelType as Record<string, RoleLabelType>)[userProps.role],
      firm: selectedFirm
        ? { id: selectedFirm.id, corporateIdentity: selectedFirm.corporateIdentity, name: selectedFirm.name }
        : null,
    };
  }

  private setFirms(firms: AuthFirmType[]): void {
    this.store.dispatch(AuthActions.setFirms({ firms }));
  }

  private serializeUserProfile(user: User | UserType): string {
    const profile = { ...user };
    delete profile.properties;
    return JSON.stringify(profile);
  }
}
